
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CollabProposal, SubmittedWork } from '@/types';
import { Button } from '@/components/ui/Button';
import { uploadWorkFile } from '@/services/uploadService';
import { AlertCircle, Loader2, UploadCloud, X, Paperclip, Download, ShieldAlert } from 'lucide-react';
import { useToast } from '@/contexts/ToastContext';
import { analyzeFileContent, MODERATION_WARNING_TEXT } from '@/services/moderationService';

interface SubmitWorkModalProps {
  isOpen: boolean;
  onClose: () => void;
  proposal: CollabProposal;
  onSubmit: (proposalId: string, submittedWorkLink: string, workDescription: string) => Promise<void>; // Ensure onSubmit returns a promise
  onBlockUser: () => void;
}

export const SubmitWorkModal: React.FC<SubmitWorkModalProps> = ({ isOpen, onClose, proposal, onSubmit, onBlockUser }) => {
  const { notify } = useToast();
  const [workDescription, setWorkDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // SURGICAL ADDITION: New state for the final submission process
  const [error, setError] = useState<string | null>(null);

  // Reset state when the modal opens
  useEffect(() => {
    if (isOpen) {
      setWorkDescription('');
      setFile(null);
      setUploadProgress(0);
      setIsUploading(false);
      setIsSubmitting(false);
      setError(null);
    }
  }, [isOpen]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setFile(event.target.files[0]);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    if (!file) {
      setError('Please select a file to submit.');
      return;
    }

    // SURGICAL REFACTOR: Separate upload and submission states for clarity and robust error handling.
    setIsUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      const analysis = await analyzeFileContent(file);
      if (analysis.violation) {
          notify(`Upload blocked: ${analysis.reason}`, 'error');
          onBlockUser();
          setIsUploading(false);
          return;
      }

      const { submittedWorkLink } = await uploadWorkFile(file, (progress) => {
        setUploadProgress(progress);
      });

      // Upload complete, now begin final submission.
      setIsUploading(false);
      setIsSubmitting(true);

      // This is the critical change. The `onSubmit` function (which is the `handleProposalAction` in the parent)
      // can now throw an error (e.g., "Can only fulfill a FUNDED proposal").
      // This `catch` block will grab that error, stop the infinite buffering, and display the error message to the user,
      // providing immediate and clear feedback.
      await onSubmit(proposal.id, submittedWorkLink, workDescription);

    } catch (finalError) {
      console.error(finalError);
      const errorMessage = finalError instanceof Error ? finalError.message : 'An unknown error occurred.';
      setError(`Submission Failed: ${errorMessage}`);
    } finally {
      // This ensures that all loading indicators are turned off, even if the submission succeeds or fails.
      setIsUploading(false);
      setIsSubmitting(false);
    }
  };

  const totalLoading = isUploading || isSubmitting;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, y: -30 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 30 }}
            className="bg-kala-800 border border-kala-700 rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col text-white"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="flex items-center justify-between p-4 border-b border-kala-700 bg-kala-900/50 rounded-t-2xl">
                <h2 className="text-xl font-bold">{`Submit Work for \"${proposal.eventName}\"`}</h2>
                <button onClick={onClose} className="p-2 rounded-full text-kala-300 hover:bg-kala-700 transition-colors"><X size={22} /></button>
            </header>

            <main className="p-6 space-y-6">
              <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3 flex items-start gap-3">
                  <ShieldAlert className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-red-200">
                      {MODERATION_WARNING_TEXT}
                  </p>
              </div>

              {proposal.submittedWork && proposal.submittedWork.length > 0 && (
                <div>
                  <h3 className="text-md font-medium text-kala-200 mb-2">Previously Submitted Files</h3>
                  <div className="max-h-40 overflow-y-auto space-y-2 rounded-lg border border-kala-700 bg-kala-900/50 p-3">
                    {proposal.submittedWork.map((work: SubmittedWork, index: number) => (
                      <div key={index} className="flex items-center justify-between text-sm p-1">
                        <p className="text-kala-300 truncate flex items-center">
                          <Paperclip className="inline w-4 h-4 mr-2 text-kala-400 flex-shrink-0" />
                          <span className="truncate">{work.workDescription || `File ${index + 1}`}</span>
                        </p>
                        <a 
                          href={work.submittedWorkLink} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="btn-secondary-sm inline-flex items-center flex-shrink-0 ml-4"
                        >
                          <Download className="w-4 h-4 mr-1"/>
                          Download
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <p className="text-sm text-kala-300">Upload a new work file and add a description. The file will be sent to the proposer for review.</p>
              
              <div>
                <label htmlFor="work-description" className="block text-sm font-medium text-kala-200 mb-1">New Submission Description</label>
                <textarea
                  id="work-description"
                  value={workDescription}
                  onChange={(e) => setWorkDescription(e.target.value)}
                  placeholder="e.g., Here is the final mix as requested. Let me know what you think!"
                  rows={3}
                  className="w-full p-2 border rounded mt-1 bg-kala-900 border-kala-600 focus:outline-none resize-y"
                  required
                />
              </div>

              <div>
                  <label htmlFor="file-upload" className="block text-sm font-medium text-kala-200 mb-1">Upload New File</label>
                  <div className="mt-2 flex justify-center rounded-lg border border-dashed border-kala-600 px-6 py-10">
                      <div className="text-center">
                          <UploadCloud className="mx-auto h-12 w-12 text-kala-400" />
                          <div className="mt-4 flex text-sm leading-6 text-kala-400">
                              <label
                                  htmlFor="file-upload"
                                  className="relative cursor-pointer rounded-md font-semibold text-blue-500 hover:text-blue-400 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2 focus-within:ring-offset-kala-900"
                              >
                                  <span>Upload a file</span>
                                  <input id="file-upload" name="file-upload" type="file" className="sr-only" onChange={handleFileChange} disabled={totalLoading} />
                              </label>
                              <p className="pl-1">or drag and drop</p>
                          </div>
                          <p className="text-xs leading-5 text-kala-500">Any file type, up to 100MB</p>
                      </div>
                  </div>
              </div>

              {file && !totalLoading && (
                <div className="text-sm text-kala-200">
                  <strong>Selected file:</strong> {file.name}
                </div>
              )}

              {isUploading && (
                <div className="w-full">
                  <div className="flex justify-between mb-1">
                      <span className="text-base font-medium text-blue-400">Uploading File...</span>
                      <span className="text-sm font-medium text-blue-400">{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-kala-700 rounded-full h-2.5">
                    <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${uploadProgress}%` }}></div>
                  </div>
                </div>
              )}

              {isSubmitting && (
                  <div className="flex items-center gap-2 text-green-400">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span className="text-sm">File uploaded! Finalizing submission...</span>
                  </div>
              )}

              {error && (
                <div className="flex items-center gap-2 text-red-400 bg-red-900/20 p-3 rounded-md">
                  <AlertCircle className="h-5 w-5" />
                  <span className="text-sm">{error}</span>
                </div>
              )}
            </main>
            
            <footer className="flex justify-end space-x-2 p-4 border-t border-kala-700 bg-kala-900/50 rounded-b-2xl">
              <Button variant="secondary" onClick={onClose} disabled={totalLoading}>Cancel</Button>
              <Button onClick={handleSubmit} disabled={totalLoading || !file || !workDescription.trim()}>
                {totalLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</> : 'Submit New File'}
              </Button>
            </footer>

          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
